import { Component, ChangeDetectionStrategy, ChangeDetectorRef, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CardModule } from 'primeng/card';

// Services and models
import { CampaignsService } from '@generated/services/campaigns.service';
import { AgentsService } from '@generated/services/agents.service';
import { NotificationService } from '@core/services/notification.service';
import { AgentSummary } from '@generated/models/agent-summary';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';

@Component({
  selector: 'app-outbound-campaign-create',
  imports: [
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantActionDirective,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    CardModule
],
  templateUrl: './outbound-campaign-create.html',
  styleUrl: './outbound-campaign-create.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutboundCampaignCreate implements OnInit {
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private notificationService = inject(NotificationService);
  private campaignsService = inject(CampaignsService);
  private agentsService = inject(AgentsService);

  campaignTitle = '';
  selectedAgentId: string | null = null;
  agents: AgentSummary[] = [];
  agentOptions: { label: string; value: string }[] = [];
  isCreating = false;

  ngOnInit() {
    this.loadAgents();
  }

  private loadAgents() {
    this.agentsService.apiAgentsGet().subscribe({
      next: (result) => {
        const untitled = $localize`:@@common.untitled:Untitled`;
        this.agents = result.agents || [];
        this.agentOptions = this.agents.map(agent => ({
          label: agent.title || agent.id || untitled,
          value: agent.id!
        }));
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load agents:', error);
        this.notificationService.showError($localize`:@@conversations.notify.agentsFail:Failed to load agents`);
      }
    });
  }

  onCreate() {
    if (!this.campaignTitle.trim()) {
      this.notificationService.showError($localize`:@@campaignCreate.notify.titleRequired:Please enter a campaign title`);
      return;
    }

    if (!this.selectedAgentId) {
      this.notificationService.showError($localize`:@@campaignCreate.notify.agentRequired:Please select an agent`);
      return;
    }

    this.isCreating = true;

    this.campaignsService.apiCampaignsPost({
      body: {
        title: this.campaignTitle,
        agent_id: this.selectedAgentId,
        campaign_type: 'Outbound'
      }
    }).subscribe({
      next: (result) => {
        this.notificationService.showSuccess($localize`:@@campaignCreate.notify.created:Campaign created`);
        this.router.navigate(['/outbound-campaigns', result.campaign_id]);
      },
      error: (error) => {
        console.error('Failed to create campaign:', error);
        this.notificationService.showError($localize`:@@campaignCreate.notify.createFail:Failed to create campaign`);
        this.isCreating = false;
        this.cdr.markForCheck();
      }
    });
  }

  onCancel() {
    this.router.navigate(['/outbound-campaigns']);
  }
}
